import { differenceInYears, isAfter, parseISO } from 'date-fns';
import { MIN_AGE } from '../constants';
/*
    Functions to validate CPR or get information about CPR.
    No interaction with external sources, only calculations and table lookups.
*/

// Don't modulus 11 check for these birthdays. See also:
// https://cpr.dk/cpr-systemet/personnumre-uden-kontrolciffer-modulus-11-kontrol/
const birthdayExceptions: string[] = [
    '010160',
    '010162',
    '010164',
    '010165',
    '010166',
    '010167',
    '010169',
    '010170',
    '010174',
    '010175',
    '010178',
    '010180',
    '010181',
    '010182',
    '010183',
    '010184',
    '010185',
    '010186',
    '010187',
    '010188',
    '010189',
    '010190',
    '010191',
    '010192',
    '010194',
    '010195'
];

// Don't modulus 11 check these birthdays (birthday in ddmmyy format)
function checkBdayExceptions(birthday: string): boolean {
    return birthdayExceptions.indexOf(birthday) !== -1;
}

export type ValidateCpr = {
    valid: boolean;
    age: number | undefined;
};

// cpr should be in the form ddmmyyxxxx
export function validateCpr(cpr: string): ValidateCpr {
    // make sure it's exactly 10 digits
    if (!/^\d{10}$/.test(cpr)) {
        return { valid: false, age: undefined };
    }

    const parts = getCprInfo(cpr);

    if (parts === undefined) {
        return { valid: false, age: undefined };
    }

    // Skip modulus 11 check for these exceptional birthdays
    if (checkBdayExceptions(parts.birthday)) {
        return { valid: true, age: parts.age };
    }
    return { valid: modulus11check(cpr), age: parts.age };
}

// Special modulus 11 check (http://da.wikipedia.org/wiki/CPR-nummer)
export function modulus11check(cpr: string): boolean {
    let modulusCheck = 4 * parseInt(cpr[0], 10);
    modulusCheck += 3 * parseInt(cpr[1], 10);
    modulusCheck += 2 * parseInt(cpr[2], 10);
    modulusCheck += 7 * parseInt(cpr[3], 10);
    modulusCheck += 6 * parseInt(cpr[4], 10);
    modulusCheck += 5 * parseInt(cpr[5], 10);
    modulusCheck += 4 * parseInt(cpr[6], 10);
    modulusCheck += 3 * parseInt(cpr[7], 10);
    modulusCheck += 2 * parseInt(cpr[8], 10);
    modulusCheck += parseInt(cpr[9], 10);
    if (modulusCheck === 0) return false;
    return modulusCheck % 11 === 0;
}

// Calc birthyear based on the two digit year and the 7th digit of CPR
function fullBirthYear(year: string, digit7: string): string {
    const yearNum = parseInt(year, 10);
    switch (digit7) {
        case '0':
        case '1':
        case '2':
        case '3':
            return `19${year}`;
        case '4':
        case '9':
            return yearNum <= 36 ? `20${year}` : `19${year}`;
        case '5':
        case '6':
        case '7':
        case '8':
            return yearNum <= 57 ? `20${year}` : `18${year}`;
    }
    return ''; // Returning blank is an error signal
}

export function calculateAge(birthDay: Date): number | undefined {
    const today = new Date(Date.now());
    if (isAfter(birthDay, today)) return undefined;
    return differenceInYears(today, birthDay);
}

type CprInfo =
    | undefined
    | {
          birthday: string;
          day: string;
          month: string;
          year: string;
          controldigits: string;
          fullyear: string;
          age: number | undefined;
      };

// Split CPR into it's many parts
function getCprInfo(v: string): CprInfo {
    //changing substr() to substring() as substr() is deprecated. substr(a, b) == s.substring(a, a+b)
    const birthday = v.substring(0, 6);
    const day = v.substring(0, 2);
    const month = v.substring(2, 4);
    const year = v.substring(4, 6);
    const controldigits = v.substring(6, 10);
    const fullyear = fullBirthYear(year, v.substring(6, 7));

    const parsed = parseISO(`${fullyear}-${month}-${day}`);
    if (isNaN(parsed.valueOf())) {
        return undefined;
    }

    const birtdayAsDate = new Date(
        parseInt(fullyear, 10),
        parseInt(month, 10) - 1, // Date has 0 based month
        parseInt(day, 10)
    );
    const age = calculateAge(birtdayAsDate);
    return { birthday, day, month, year, fullyear, controldigits, age };
}

export const isValidAge = (age: number) => age >= MIN_AGE;
